import { inject, Injectable, InjectionToken } from '@angular/core';
import { Router } from '@angular/router';
import type { AppPlugin } from '@capacitor/app';
import type { BrowserPlugin } from '@capacitor/browser';
import { environment } from '../../../environments/environment';
import { IS_NATIVE_SHELL } from './live-update.service';
import { LoggerService } from './logger.service';
import { LOCAL_STORAGE } from './user-storage.service';

/** The app's custom URL scheme — registered in AndroidManifest.xml / Info.plist. */
export const NATIVE_AUTH_CALLBACK = 'com.gardenbaysoftware.checkpoint://oauth-callback';
const VERIFIER_KEY = 'nativeAuth.verifier';

/**
 * Loads the plugins lazily (native shell only). Resolves to a plain object holding
 * them — never a plugin itself, which a promise would treat as a thenable.
 */
export const NATIVE_AUTH_PLUGINS = new InjectionToken<
  () => Promise<{ App: AppPlugin; Browser: BrowserPlugin }>
>('NATIVE_AUTH_PLUGINS', {
  providedIn: 'root',
  factory: () => async () => {
    const [{ App }, { Browser }] = await Promise.all([import('@capacitor/app'), import('@capacitor/browser')]);
    return { App, Browser };
  },
});

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Google sign-in inside the Capacitor shells. Google refuses OAuth in WebViews, so:
 *
 * 1. login() opens /auth/google?client=native&challenge=… in a system browser tab
 *    (Chrome Custom Tab / SFSafariViewController), keeping a random verifier whose
 *    SHA-256 is the challenge.
 * 2. The backend's OAuth callback redirects to com.gardenbaysoftware.checkpoint://
 *    oauth-callback?code=… — a 60 s single-use code, never the JWT.
 * 3. The app catches that link, closes the tab, and exchanges code + verifier at
 *    POST /auth/native/exchange for the JWT, then finishes sign-in on the normal
 *    /oauth-callback route (same handling as the web).
 *
 * See backend/src/utils/native-auth.ts and docs/features/capacitor-spike.md.
 */
@Injectable({ providedIn: 'root' })
export class NativeAuthService {
  private readonly log = inject(LoggerService).getLogger('native-auth');
  private readonly isNative = inject(IS_NATIVE_SHELL);
  private readonly loadPlugins = inject(NATIVE_AUTH_PLUGINS);
  private readonly storage = inject(LOCAL_STORAGE);
  private readonly router = inject(Router);
  private readonly authBaseUrl = environment.apiUrl.replace('/api', '');
  private readonly handledUrls = new Set<string>();
  private plugins: { App: AppPlugin; Browser: BrowserPlugin } | null = null;

  /** Call at app start so the sign-in return link is caught — also on a cold start. */
  async init(): Promise<void> {
    if (!this.isNative || this.plugins) return;
    try {
      this.plugins = await this.loadPlugins();
      await this.plugins.App.addListener('appUrlOpen', ({ url }) => this.handleUrl(url));
      const launch = await this.plugins.App.getLaunchUrl();
      if (launch?.url) await this.handleUrl(launch.url);
    } catch (error) {
      this.log.error('Native auth init failed:', error);
    }
  }

  async login(invitationToken?: string): Promise<void> {
    await this.init();
    if (!this.plugins) throw new Error('Native sign-in is unavailable');

    const verifier = base64Url(crypto.getRandomValues(new Uint8Array(32)));
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    // Persisted, not in memory: the app may be backgrounded or even killed while the tab is open.
    this.storage.setItem(VERIFIER_KEY, verifier);

    const url = new URL(`${this.authBaseUrl}/auth/google`);
    url.searchParams.set('client', 'native');
    url.searchParams.set('challenge', base64Url(new Uint8Array(digest)));
    if (invitationToken) url.searchParams.set('invitation_token', invitationToken);

    this.log.info('Opening Google sign-in in the system browser');
    await this.plugins.Browser.open({ url: url.toString() });
  }

  private async handleUrl(url: string): Promise<void> {
    if (!url.startsWith(NATIVE_AUTH_CALLBACK) || this.handledUrls.has(url)) return;
    this.handledUrls.add(url); // launch URL and appUrlOpen can both deliver it

    await this.plugins?.Browser.close().catch(() => undefined);
    const params = new URL(url).searchParams;
    const token = await this.redeem(params.get('code'), params.get('error'));

    await this.router.navigate(['/oauth-callback'], {
      queryParams: token ? { token } : { error: 'auth_failed' },
    });
  }

  private async redeem(code: string | null, error: string | null): Promise<string | null> {
    const verifier = this.storage.getItem(VERIFIER_KEY);
    this.storage.removeItem(VERIFIER_KEY);
    if (error || !code || !verifier) {
      this.log.warn(`Google sign-in did not complete (${error ?? (code ? 'no verifier' : 'no code')})`);
      return null;
    }
    try {
      const response = await fetch(`${this.authBaseUrl}/auth/native/exchange`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, verifier }),
      });
      const body = (await response.json()) as { token?: string; error?: string };
      if (!response.ok || !body.token) {
        this.log.warn(`Sign-in code exchange rejected: ${body.error ?? response.status}`);
        return null;
      }
      this.log.info('Google sign-in completed via native code exchange');
      return body.token;
    } catch (fetchError) {
      this.log.error('Sign-in code exchange failed:', fetchError);
      return null;
    }
  }
}
